/**
 * Reading somebody else's spreadsheet.
 *
 * A contractor moving off Housecall Pro, Jobber or a decade of Excel arrives
 * with a file, and that file is the first thing Crewlark will ever be judged
 * on. So the rule throughout here is: never reject a file for being untidy,
 * and never silently change what it says. Every row either becomes a record
 * or comes back with a sentence explaining what was wrong with it.
 *
 * Three separable pieces, all dependency-free so they run in the browser
 * before anything is sent anywhere:
 *
 *   parseCsv      the file, as rows of strings
 *   guessMapping  which column probably feeds which field
 *   buildRows     the mapping applied, with per-row complaints
 *
 * The mapping is a guess the person corrects, never a decision made for them.
 * Exports name the same column "Phone", "Phone Number", "Primary Phone",
 * "phone_1" and "Mobile", and a guess that is right most of the time and
 * silent when wrong is worse than no guess at all — so the UI always shows
 * what was guessed and lets it be changed.
 */
import { normalizePhone } from './format.js';
import { looksLikeFullAddress, parseAddress } from './address-parse.js';
import { parseCents } from './money.js';
import { JOB_STATUSES } from './job-status.js';
/* --- the file ------------------------------------------------------------ */
/**
 * CSV into rows of strings.
 *
 * Hand-written rather than pulled from npm, because @fsm/shared is
 * dependency-free on purpose and because the format is small: fields split on
 * commas, a field wrapped in double quotes may contain commas and newlines,
 * and a doubled quote inside a quoted field is one literal quote. That is the
 * whole of RFC 4180 that matters here.
 *
 * Handles the three line endings in the wild (\n, \r\n, \r), strips a UTF-8
 * BOM — Excel writes one, and without this the first header comes back as
 * "﻿Name" and never matches anything — and drops a trailing blank line
 * so a file ending in a newline does not import one empty record.
 *
 * It also unwraps Excel's text-forcing syntax; see `unwrapExcelText`.
 */
export function parseCsv(text) {
    const source = text.replace(/^﻿/, '');
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    let i = 0;
    const endField = () => {
        row.push(unwrapExcelText(field));
        field = '';
    };
    const endRow = () => {
        endField();
        rows.push(row);
        row = [];
    };
    while (i < source.length) {
        const c = source[i];
        if (quoted) {
            if (c === '"') {
                if (source[i + 1] === '"') {
                    field += '"';
                    i += 2;
                    continue;
                }
                quoted = false;
                i += 1;
                continue;
            }
            field += c;
            i += 1;
            continue;
        }
        if (c === '"') {
            quoted = true;
            i += 1;
            continue;
        }
        if (c === ',') {
            endField();
            i += 1;
            continue;
        }
        if (c === '\r') {
            /* \r\n is one ending, not two. */
            endRow();
            i += source[i + 1] === '\n' ? 2 : 1;
            continue;
        }
        if (c === '\n') {
            endRow();
            i += 1;
            continue;
        }
        field += c;
        i += 1;
    }
    /* Whatever is left is the last row, unless the file ended on a newline. */
    if (field.length > 0 || row.length > 0)
        endRow();
    /*
     * Rows that are entirely empty. Spreadsheets love to export a few, and a
     * blank row is never a customer.
     */
    return rows.filter((r) => r.some((cell) => cell.trim().length > 0));
}
/**
 * Excel's text-forcing wrapper, removed.
 *
 * ## The failure this exists for
 *
 * A real export arrived with every cell written as `="8"`. That is Excel's
 * way of saying "this is text, do not turn it into a number or a date", and
 * exporters that are careful about leading zeros and long account numbers
 * emit it for whole columns at a time.
 *
 * To an RFC 4180 parser it is not syntax. The `=` is an ordinary character,
 * so the field comes through as `=8` or `="8"` depending on how the exporter
 * quoted it, and everything downstream reads that as the value.
 *
 * What it cost, in one import of 178 jobs: every job was titled `="8"`,
 * `="12"`, `="21"` — the old system's job numbers wearing their wrapper.
 * Every date failed to parse and came through empty, so no job had a
 * schedule. Every status failed to match and fell back to `unscheduled`, so
 * a ledger of finished work landed looking like 178 jobs nobody had got
 * round to booking. One unparsed character, three separate wrong answers,
 * and not a single error anywhere — the import reported 178 successes.
 *
 * ## Both shapes
 *
 * Which one arrives depends on whether the exporter also quoted the field:
 *
 *     ="8"           → the parser sees `=` then a quoted `8`   → `=8`
 *     "=""8"""       → all one quoted field containing `="8"`  → `="8"`
 *
 * Both are handled. Nothing else is touched: a value is only unwrapped when
 * the `=` is followed by a quoted run that reaches the end of the field,
 * which no genuine value does. `=` on its own, `a=b`, and `=> go` are left
 * exactly as they are.
 */
export function unwrapExcelText(field) {
    /* "=""8"""  →  ="8"  →  8 */
    const quoted = /^="(.*)"$/s.exec(field);
    if (quoted)
        return quoted[1];
    /*
     * `="8"` parsed to `=8`. The quotes are gone by the time we see it, so the
     * only evidence left is the leading `=`. Removed only when what follows is
     * not itself formula-shaped — a cell holding a real spreadsheet formula is
     * data we have no business rewriting, and `=SUM(A1:A2)` should survive to
     * be rejected honestly rather than become `SUM(A1:A2)`.
     */
    if (field.startsWith('=') && field.length > 1 && !/^=[A-Z]+\(/.test(field)) {
        return field.slice(1);
    }
    return field;
}
const ADDRESS_FIELDS = [
    {
        key: 'line1',
        label: 'Street address',
        required: false,
        aliases: ['address line 1', 'address1', 'street address', 'service address', 'street', 'address'],
    },
    { key: 'line2', label: 'Unit or floor', required: false, aliases: ['address line 2', 'address2', 'unit', 'suite', 'apt'] },
    { key: 'city', label: 'City', required: false, aliases: ['city', 'town'] },
    { key: 'state', label: 'State', required: false, aliases: ['state', 'province', 'region'] },
    {
        key: 'postalCode',
        label: 'ZIP',
        required: false,
        aliases: ['postal code', 'postcode', 'zip code', 'zip'],
    },
];
export const CUSTOMER_FIELDS = [
    /*
     * Two name columns, because most real exports have two.
     *
     * The first file a contractor actually brought here had `First Name` and
     * `Last Name` as separate columns, and with one name field the guess took
     * `First Name` and quietly imported 878 customers with no surnames. Nothing
     * complained: a first name is a perfectly valid name. The import would have
     * looked like it worked.
     *
     * So `nameLast` exists and is joined on with a space when it is mapped. A
     * file with one `Name` column leaves it empty and nothing changes.
     */
    {
        key: 'name',
        label: 'Customer name',
        hint: 'or first name, if your file splits them',
        required: true,
        aliases: ['customer name', 'client name', 'full name', 'company name', 'name', 'first name'],
    },
    {
        key: 'nameLast',
        label: 'Last name',
        hint: 'joined onto the name above',
        required: false,
        aliases: ['last name', 'surname', 'family name'],
    },
    { key: 'phone', label: 'Phone', required: false, aliases: ['mobile', 'cell', 'phone number', 'primary phone', 'phone', 'telephone'] },
    { key: 'email', label: 'Email', required: false, aliases: ['email address', 'e-mail', 'email'] },
    ...ADDRESS_FIELDS,
    { key: 'notes', label: 'Notes', required: false, aliases: ['notes', 'comments', 'description'] },
];
export const JOB_FIELDS = [
    {
        key: 'title',
        label: 'Job title',
        hint: 'what the work was',
        required: true,
        aliases: ['job title', 'job name', 'work order', 'title', 'summary'],
    },
    {
        key: 'customerName',
        label: 'Customer name',
        hint: 'created if this customer is new — or first name, if split',
        required: true,
        aliases: ['customer name', 'client name', 'customer', 'client', 'first name'],
    },
    {
        key: 'customerNameLast',
        label: 'Customer last name',
        hint: 'joined onto the name above',
        required: false,
        aliases: ['last name', 'surname', 'family name'],
    },
    { key: 'customerPhone', label: 'Customer phone', hint: 'used to match an existing customer', required: false, aliases: ['customer phone', 'phone number', 'mobile', 'cell', 'phone'] },
    { key: 'customerEmail', label: 'Customer email', required: false, aliases: ['customer email', 'email address', 'email'] },
    ...ADDRESS_FIELDS,
    {
        key: 'jobType',
        label: 'Job type',
        hint: 'what the work is — added to your list if new',
        required: false,
        aliases: ['job type', 'service type', 'work type', 'service', 'trade', 'category', 'type'],
    },
    { key: 'description', label: 'Description', required: false, aliases: ['description', 'notes', 'details', 'comments'] },
    {
        key: 'scheduledStart',
        label: 'Scheduled start',
        hint: 'left unscheduled if blank',
        required: false,
        aliases: ['scheduled start', 'start date', 'start time', 'scheduled', 'date', 'start'],
    },
    { key: 'scheduledEnd', label: 'Scheduled end', required: false, aliases: ['scheduled end', 'end date', 'end time', 'end'] },
    {
        key: 'priority',
        label: 'Priority',
        hint: 'low, normal, high or emergency',
        required: false,
        aliases: ['priority', 'urgency'],
    },
    {
        key: 'status',
        label: 'Status',
        hint: 'completed, cancelled, scheduled…',
        required: false,
        aliases: ['job status', 'status', 'state', 'stage'],
    },
    {
        key: 'amount',
        label: 'Job amount',
        hint: 'becomes a single line item, so the job is worth what it was',
        required: false,
        aliases: ['job amount', 'total', 'amount', 'price', 'value', 'revenue'],
    },
];
export function fieldsFor(entity) {
    return entity === 'customers' ? CUSTOMER_FIELDS : JOB_FIELDS;
}
/** Header text reduced to the part worth matching on. */
function normalizeHeader(header) {
    return header
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/[^a-z0-9 ]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * A first guess at which column feeds which field.
 *
 * Exact header match wins over a substring, and an earlier alias wins over a
 * later one — which is why the alias lists above are ordered specific-first.
 * A column is used at most once: without that, a file with "Phone" and
 * "Customer Phone" hands both fields the same column and the second is
 * silently wrong.
 *
 * Every unmatched field comes back as null rather than being omitted, so the
 * UI can list what it could not work out rather than quietly dropping it.
 */
export function guessMapping(headers, entity) {
    const normalized = headers.map(normalizeHeader);
    const taken = new Set();
    const mapping = {};
    for (const field of fieldsFor(entity)) {
        let best = null;
        /*
         * Aliases go through the same normaliser as the headers. They are written
         * above the way a person would say them — "e-mail", "address line 1" —
         * and a header is normalised to "e mail" before matching, so comparing
         * the raw alias never matches anything with punctuation in it. That bug
         * cost `E-Mail` columns silently going unmapped.
         */
        const aliases = field.aliases.map(normalizeHeader);
        /* Exact first, across every alias, before any substring match. */
        for (const alias of aliases) {
            const at = normalized.findIndex((h, i) => !taken.has(i) && h === alias);
            if (at !== -1) {
                best = at;
                break;
            }
        }
        if (best === null) {
            for (const alias of aliases) {
                const at = normalized.findIndex((h, i) => !taken.has(i) && h.includes(alias));
                if (at !== -1) {
                    best = at;
                    break;
                }
            }
        }
        if (best !== null)
            taken.add(best);
        mapping[field.key] = best;
    }
    return mapping;
}
/** The cell for a field, trimmed, or '' when the field has no column. */
function cell(row, mapping, key) {
    const at = mapping[key];
    if (at === null || at === undefined)
        return '';
    return (row[at] ?? '').trim();
}
/**
 * The two name columns as one name.
 *
 * Trimmed and joined with a single space, skipping whichever is empty — a
 * file where only some rows have a surname gives "Dana Whitlock" and "Dana",
 * not "Dana " with a trailing space that sorts oddly and reads as a typo.
 */
function joinName(first, last) {
    return [first, last].filter(Boolean).join(' ');
}
/** An email only if it could be one. A wrong guess here is worse than none. */
function emailOrNull(value) {
    if (!value)
        return null;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : null;
}
/**
 * The address, all-or-nothing.
 *
 * A property needs a street, a city, a state and a postcode — the schema says
 * so and a tech needs all four to find the house. A row with a street and
 * nothing else is not half an address, it is a row whose address columns were
 * mapped wrong, so it comes back as a problem rather than a property.
 *
 * No address columns at all is different, and fine: that is a contact list.
 */
function buildAddress(row, mapping, line, problems) {
    let line1 = cell(row, mapping, 'line1');
    let city = cell(row, mapping, 'city');
    let state = cell(row, mapping, 'state');
    let postalCode = cell(row, mapping, 'postalCode');
    /*
     * One column holding the whole address.
     *
     * Common in a jobs export — "412 Buckthorn Ln, Austin, TX 78702" in a
     * single `Address` field — and until this, such a file produced a street
     * with no city and the row lost its address entirely. Worse here than in a
     * contacts import: the postcode is half of how a job finds the customer it
     * belongs to, so losing it means a duplicate customer rather than a tidy-up.
     *
     * Only when the other three are unmapped, so a file that does split them
     * is never second-guessed.
     */
    if (line1 && !city && !state && !postalCode && looksLikeFullAddress(line1)) {
        const parsed = parseAddress(line1);
        if (parsed.city && parsed.postalCode) {
            line1 = parsed.line1;
            city = parsed.city;
            state = parsed.state;
            postalCode = parsed.postalCode;
        }
    }
    const any = line1 || city || state || postalCode;
    if (!any)
        return null;
    const missing = [
        !line1 && 'street',
        !city && 'city',
        !state && 'state',
        !postalCode && 'ZIP',
    ].filter(Boolean);
    if (missing.length > 0) {
        problems.push({
            line,
            message: `address is missing ${missing.join(', ')} — imported without an address`,
        });
        return null;
    }
    return {
        line1,
        line2: cell(row, mapping, 'line2') || null,
        city,
        state,
        postalCode,
    };
}
/**
 * A date out of a spreadsheet.
 *
 * Accepts ISO (what an export written by a programmer produces) and the US
 * forms a person types: M/D/YYYY and M-D-YYYY, with an optional time, with or
 * without am/pm. Returns an ISO string with no timezone suffix, because the
 * date in the file is the contractor's local wall clock and attaching UTC to
 * it would move every appointment by the offset.
 *
 * Day-first formats are deliberately not guessed. 03/04/2026 is the fourth of
 * March in one country and the third of April in another, and a silent wrong
 * answer on half the rows is the worst possible outcome — better that the
 * person sees American dates and tells us if that is wrong.
 */
export function parseImportDate(value) {
    const text = value.trim();
    if (!text)
        return null;
    /* Already ISO-ish: 2026-03-04, optionally with a time. */
    const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(text);
    if (iso) {
        const [, y, mo, d, h = '0', mi = '0'] = iso;
        return isoFrom(Number(y), Number(mo), Number(d), Number(h), Number(mi));
    }
    const us = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:[ ,]+(\d{1,2}):(\d{2})(?::\d{2})?\s*([ap])\.?m\.?)?/i.exec(text);
    if (us) {
        const [, mo, d, rawYear, h, mi, half] = us;
        let year = Number(rawYear);
        if (year < 100)
            year += year < 70 ? 2000 : 1900;
        let hour = h ? Number(h) : 0;
        if (half) {
            const pm = half.toLowerCase() === 'p';
            if (hour === 12)
                hour = pm ? 12 : 0;
            else if (pm)
                hour += 12;
        }
        return isoFrom(year, Number(mo), Number(d), hour, mi ? Number(mi) : 0);
    }
    return null;
}
function isoFrom(y, mo, d, h, mi) {
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59)
        return null;
    const pad = (n, width = 2) => String(n).padStart(width, '0');
    /* Round-trip through Date to reject the 31st of February and friends. */
    const probe = new Date(Date.UTC(y, mo - 1, d));
    if (probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d)
        return null;
    return `${pad(y, 4)}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}:00`;
}
const PRIORITIES = new Set(['low', 'normal', 'high', 'emergency']);
/**
 * Somebody else's status words, mapped onto ours.
 *
 * Every CRM names these differently and most offer more than one word for the
 * same thing. The point is not to be exhaustive — it is that importing a
 * ledger of finished work must not drop eight hundred completed jobs onto the
 * dispatch board as outstanding, which is what happens when status is ignored.
 *
 * An unrecognised word is reported rather than guessed at, and the row still
 * imports: a job with the wrong status is fixable in a click, a job that was
 * refused is not there at all.
 */
const STATUS_WORDS = [
    ['completed', ['completed', 'complete', 'done', 'finished', 'closed', 'closed won']],
    ['cancelled', ['cancelled', 'canceled', 'void', 'voided', 'closed lost', 'declined']],
    ['paid', ['paid', 'paid in full', 'settled']],
    ['invoiced', ['invoiced', 'billed', 'awaiting payment', 'unpaid']],
    ['in_progress', ['in progress', 'in-progress', 'started', 'working', 'on site', 'en route']],
    ['scheduled', ['scheduled', 'booked', 'confirmed', 'dispatched', 'assigned']],
    ['unscheduled', ['unscheduled', 'new', 'open', 'pending', 'to schedule', 'lead']],
];
export function parseImportStatus(value) {
    const text = value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
    if (!text)
        return null;
    /* Our own names first, so "completed" never goes through the word lists. */
    if (JOB_STATUSES.includes(text))
        return text;
    for (const [status, words] of STATUS_WORDS) {
        if (words.includes(text))
            return status;
    }
    return null;
}
export function buildCustomerRows(dataRows, mapping) {
    const rows = [];
    const problems = [];
    dataRows.forEach((row, index) => {
        const line = index + 2; /* header is line 1 */
        const name = joinName(cell(row, mapping, 'name'), cell(row, mapping, 'nameLast'));
        if (!name) {
            problems.push({ line, message: 'no customer name — row skipped' });
            return;
        }
        const rawEmail = cell(row, mapping, 'email');
        const email = emailOrNull(rawEmail);
        if (rawEmail && !email) {
            problems.push({ line, message: `"${rawEmail}" is not an email address — imported without it` });
        }
        const phone = cell(row, mapping, 'phone');
        rows.push({
            line,
            name,
            phone: phone || null,
            email,
            notes: cell(row, mapping, 'notes') || null,
            property: buildAddress(row, mapping, line, problems),
        });
    });
    return { rows, problems };
}
export function buildJobRows(dataRows, mapping) {
    const rows = [];
    const problems = [];
    dataRows.forEach((row, index) => {
        const line = index + 2;
        const title = cell(row, mapping, 'title');
        if (!title) {
            problems.push({ line, message: 'no job title — row skipped' });
            return;
        }
        const customerName = joinName(cell(row, mapping, 'customerName'), cell(row, mapping, 'customerNameLast'));
        if (!customerName) {
            problems.push({ line, message: 'no customer name — row skipped' });
            return;
        }
        const rawPriority = cell(row, mapping, 'priority').toLowerCase();
        let priority = 'normal';
        if (rawPriority) {
            if (PRIORITIES.has(rawPriority))
                priority = rawPriority;
            else
                problems.push({ line, message: `"${rawPriority}" is not a priority — set to normal` });
        }
        const rawStart = cell(row, mapping, 'scheduledStart');
        const scheduledStart = parseImportDate(rawStart);
        if (rawStart && !scheduledStart) {
            problems.push({ line, message: `could not read the date "${rawStart}" — left unscheduled` });
        }
        const rawEnd = cell(row, mapping, 'scheduledEnd');
        const scheduledEnd = parseImportDate(rawEnd);
        if (rawEnd && !scheduledEnd) {
            problems.push({ line, message: `could not read the end date "${rawEnd}"` });
        }
        const rawStatus = cell(row, mapping, 'status');
        const status = parseImportStatus(rawStatus);
        if (rawStatus && !status) {
            problems.push({
                line,
                message: `"${rawStatus}" is not a status we recognise — imported as unscheduled`,
            });
        }
        const rawAmount = cell(row, mapping, 'amount');
        const amountCents = rawAmount ? parseCents(rawAmount) : null;
        if (rawAmount && amountCents === null) {
            problems.push({ line, message: `could not read the amount "${rawAmount}"` });
        }
        const rawEmail = cell(row, mapping, 'customerEmail');
        rows.push({
            line,
            title,
            jobType: cell(row, mapping, 'jobType') || null,
            status,
            amountCents: amountCents !== null && amountCents > 0 ? amountCents : null,
            description: cell(row, mapping, 'description') || null,
            priority,
            scheduledStart,
            /* An end before its start is not a window; drop it rather than store it. */
            scheduledEnd: scheduledEnd && scheduledStart && scheduledEnd > scheduledStart ? scheduledEnd : null,
            customer: {
                name: customerName,
                phone: cell(row, mapping, 'customerPhone') || null,
                email: emailOrNull(rawEmail),
                property: buildAddress(row, mapping, line, problems),
            },
        });
    });
    return { rows, problems };
}
/* --- matching an existing customer --------------------------------------- */
/**
 * A key for recognising a customer we already have.
 *
 * Phone first: it is the one thing a contractor's records almost always have
 * and almost never share between two customers. Then email. Name is
 * deliberately last and only with a postcode beside it, because "Smith"
 * appears forty times in a real book of business and merging two of them is
 * the one import mistake that cannot be undone by deleting a row.
 *
 * Returns null when there is nothing safe to match on, which means "make a
 * new customer" — a duplicate is an annoyance, a merge is a disaster.
 */
export function customerMatchKey(input) {
    const digits = normalizePhone(input.phone);
    if (digits.length >= 7)
        return `phone:${digits.slice(-10)}`;
    const email = (input.email ?? '').trim().toLowerCase();
    if (email)
        return `email:${email}`;
    const name = (input.name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    const postcode = (input.postalCode ?? '').trim().toLowerCase();
    if (name && postcode)
        return `name:${name}|${postcode}`;
    return null;
}
/** How many rows one upload may carry. */
export const IMPORT_ROW_LIMIT = 2000;
//# sourceMappingURL=csv-import.js.map